// =============================================================================
// PAYMENT SCREEN COMPONENT
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, Shield, Clock, CheckCircle, AlertCircle, Calendar, DollarSign } from 'lucide-react';
import { Button } from '@/components/Button';
import { TimeSelector } from '@/components/TimeSelector';
import { paymentService } from '@/services/payment';
import { availabilityService, type TimeSlot } from '@/services/availabilityService';
import type { PaymentConfig } from '@/types';

interface PaymentScreenProps {
  className?: string;
}

interface PaymentFormData {
  email: string;
  name: string;
  company?: string;
  shippingAddress: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}

type PaymentMethod = 'stripe' | 'other';

export const PaymentScreen: React.FC<PaymentScreenProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [formData, setFormData] = useState<PaymentFormData>({
    email: '',
    name: '',
    company: '',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'details' | 'payment' | 'processing'>('details');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('stripe');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);

  // Parse payment config from URL params
  useEffect(() => {
    try {
      const configParam = searchParams.get('config');
      if (configParam) {
        const config = JSON.parse(decodeURIComponent(configParam));
        setPaymentConfig(config);
      }
    } catch (err) {
      setError('Invalid payment configuration');
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested shipping address fields
    if (name.startsWith('shipping.')) {
      const field = name.replace('shipping.', '') as keyof PaymentFormData['shippingAddress'];
      setFormData(prev => ({
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProceedToPayment = async () => {
    if (!paymentConfig || !formData.email || !formData.name) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate shipping address
    const { shippingAddress } = formData;
    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip || !shippingAddress.country) {
      setError('Please fill in all shipping address fields');
      return;
    }

    if (!selectedTimeSlot) {
      setError('Please select an appointment time slot');
      return;
    }

    if (paymentMethod === 'stripe') {
      setStep('payment');
      setIsLoading(true);
      setError(null);

      try {
        // Create checkout session using payment service with appointment details and shipping address
        const response = await paymentService.createCheckoutSession({
          paymentConfig: {
            ...paymentConfig,
            metadata: {
              ...paymentConfig.metadata,
              appointmentDate: selectedTimeSlot.start,
              appointmentEndDate: selectedTimeSlot.end,
              timezone: selectedTimeSlot.timezone,
              company: formData.company || '',
              paymentMethod: 'stripe',
              shippingStreet: formData.shippingAddress.street,
              shippingCity: formData.shippingAddress.city,
              shippingState: formData.shippingAddress.state,
              shippingZip: formData.shippingAddress.zip,
              shippingCountry: formData.shippingAddress.country,
            }
          },
          customerEmail: formData.email,
          customerName: formData.name,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/payment`,
        });

        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Payment failed');
        setStep('details');
        setIsLoading(false);
      }
    } else {
      // Handle "Book without paying upfront" option
      await handleBookWithoutPayment();
    }
  };

  const handleBookWithoutPayment = async () => {
    if (!selectedTimeSlot) {
      setError('Please select an appointment time slot');
      return;
    }

    // Validate shipping address
    const { shippingAddress } = formData;
    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip || !shippingAddress.country) {
      setError('Please fill in all shipping address fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a booking request for the selected time slot
      const bookingRequest = {
        clientName: formData.name,
        clientEmail: formData.email,
        clientPhone: '', // Not required for this flow
        startTime: selectedTimeSlot.start,
        endTime: selectedTimeSlot.end,
        timezone: selectedTimeSlot.timezone,
        notes: `Payment method: Other (to be arranged). Service: ${paymentConfig?.name}. Company: ${formData.company || 'N/A'}. Shipping Address: ${formData.shippingAddress.street}, ${formData.shippingAddress.city}, ${formData.shippingAddress.state} ${formData.shippingAddress.zip}, ${formData.shippingAddress.country}`,
        meetingType: 'session',
        paymentMethod: 'other' as const,
        serviceName: paymentConfig?.name || '',
        serviceAmount: paymentConfig?.amount || 0,
        company: formData.company || '',
        shippingAddress: formData.shippingAddress
      };

      const response = await availabilityService.bookTimeSlot(bookingRequest);

      if (response.success) {
        // Redirect to success page with booking confirmation
        navigate(`/payment/success?booking_id=${response.callId}&payment_method=other`);
      } else {
        setError(response.error || 'Failed to book appointment');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const getPaymentTypeDisplay = (type: string) => {
    switch (type) {
      case 'one-time':
        return 'One-time Payment';
      case 'recurring-weekly':
        return 'Weekly Subscription';
      case 'recurring-monthly':
        return 'Monthly Subscription';
      case 'recurring-yearly':
        return 'Yearly Subscription';
      default:
        return type;
    }
  };

  const getCategoryDisplay = (category: string) => {
    switch (category) {
      case 'professional-services':
        return 'Professional Services';
      case 'subscription-content':
        return 'Subscription Content';
      case 'course-package':
        return 'Course Package';
      case 'consultation':
        return 'Consultation';
      default:
        return category;
    }
  };

  if (!paymentConfig) {
    return (
      <div className={`payment-screen ${className}`}>
        <div className="container">
          <div className="payment-error">
            <AlertCircle size={48} className="error-icon" />
            <h2>Payment Configuration Error</h2>
            <p>Unable to load payment configuration. Please try again.</p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/')}
            >
              Return Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`payment-screen ${className}`}>
      <div className="container">
        <div className="payment-header">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(-1)}
            className="back-button"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1>Complete Your Payment</h1>
        </div>

        <div className="payment-content">
          <div className="payment-summary">
            <div className="summary-card">
              <h3>Order Summary</h3>
              <div className="summary-details">
                <div className="summary-item">
                  <span className="label">Service:</span>
                  <span className="value">{paymentConfig.name}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Category:</span>
                  <span className="value">{getCategoryDisplay(paymentConfig.category)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Type:</span>
                  <span className="value">{getPaymentTypeDisplay(paymentConfig.type)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Amount:</span>
                  <span className="value amount">{formatAmount(paymentConfig.amount)}</span>
                </div>
              </div>
              {paymentConfig.description && (
                <div className="summary-description">
                  <p>{paymentConfig.description}</p>
                </div>
              )}
            </div>

            <div className="security-features">
              <div className="security-item">
                <Shield size={20} />
                <span>256-bit SSL encryption</span>
              </div>
              <div className="security-item">
                <CreditCard size={20} />
                <span>Secure payment processing</span>
              </div>
              <div className="security-item">
                <CheckCircle size={20} />
                <span>PCI compliant</span>
              </div>
            </div>
          </div>

          <div className="payment-form">
            {step === 'details' && (
              <div className="form-step">
                <h3>Contact Information</h3>
                <p className="form-description">
                  We'll use this information to send you a receipt and important updates about your purchase.
                </p>

                {error && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company (Optional)</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your Company"
                  />
                </div>

                <div className="shipping-section">
                  <h4>Shipping Address</h4>
                  <div className="shipping-notice">
                    <CheckCircle size={16} />
                    <span>Free shipping and handling on all orders</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="shipping.street">Street Address *</label>
                    <input
                      type="text"
                      id="shipping.street"
                      name="shipping.street"
                      value={formData.shippingAddress.street}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="shipping.city">City *</label>
                      <input
                        type="text"
                        id="shipping.city"
                        name="shipping.city"
                        value={formData.shippingAddress.city}
                        onChange={handleInputChange}
                        placeholder="New York"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="shipping.state">State *</label>
                      <input
                        type="text"
                        id="shipping.state"
                        name="shipping.state"
                        value={formData.shippingAddress.state}
                        onChange={handleInputChange}
                        placeholder="NY"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="shipping.zip">ZIP Code *</label>
                      <input
                        type="text"
                        id="shipping.zip"
                        name="shipping.zip"
                        value={formData.shippingAddress.zip}
                        onChange={handleInputChange}
                        placeholder="10001"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="shipping.country">Country *</label>
                    <select
                      id="shipping.country"
                      name="shipping.country"
                      value={formData.shippingAddress.country}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="MX">Mexico</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="IT">Italy</option>
                      <option value="ES">Spain</option>
                      <option value="NL">Netherlands</option>
                      <option value="BE">Belgium</option>
                      <option value="CH">Switzerland</option>
                      <option value="AT">Austria</option>
                      <option value="SE">Sweden</option>
                      <option value="NO">Norway</option>
                      <option value="DK">Denmark</option>
                      <option value="FI">Finland</option>
                      <option value="PL">Poland</option>
                      <option value="PT">Portugal</option>
                      <option value="IE">Ireland</option>
                      <option value="NZ">New Zealand</option>
                      <option value="JP">Japan</option>
                      <option value="KR">South Korea</option>
                      <option value="SG">Singapore</option>
                      <option value="HK">Hong Kong</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="form-group">
                  <label>Payment Method</label>
                  <div className="payment-method-selector">
                    <button
                      type="button"
                      className={`payment-method-option ${paymentMethod === 'stripe' ? 'selected' : ''}`}
                      onClick={() => setPaymentMethod('stripe')}
                    >
                      <CreditCard size={20} />
                      <div className="option-content">
                        <span className="option-title">Pay with Stripe</span>
                        <span className="option-description">Secure credit card payment</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`payment-method-option ${paymentMethod === 'other' ? 'selected' : ''}`}
                      onClick={() => setPaymentMethod('other')}
                    >
                      <DollarSign size={20} />
                      <div className="option-content">
                        <span className="option-title">Other Payment</span>
                        <span className="option-description">Cash, Zelle, Venmo, Wise, or PayPal (arrange later)</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Time Selector */}
                <TimeSelector
                  selectedDate={selectedDate}
                  selectedTimeSlot={selectedTimeSlot}
                  onDateChange={setSelectedDate}
                  onTimeSlotSelect={setSelectedTimeSlot}
                  disabled={false}
                  className="payment-time-selector"
                />

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleProceedToPayment}
                  loading={isLoading}
                  className="payment-button"
                  disabled={!selectedTimeSlot}
                >
                  {paymentMethod === 'stripe' ? (
                    <>
                      <CreditCard size={20} />
                      Proceed to Payment
                    </>
                  ) : (
                    <>
                      <Calendar size={20} />
                      Book Without Paying Upfront
                    </>
                  )}
                </Button>

                <div className="payment-note">
                  <p>
                    <strong>Note:</strong> Please select an appointment time above. 
                    {paymentMethod === 'other' && (
                      <> You will need to arrange payment (Cash, Zelle, Venmo, Wise, or PayPal) 
                      before or at the time of your appointment. We will contact you to confirm 
                      payment details after booking.</>
                    )}
                  </p>
                </div>
              </div>
            )}

            {step === 'processing' && (
              <div className="processing-step">
                <div className="processing-spinner">
                  <Clock size={48} className="spinning" />
                </div>
                <h3>Processing Your Payment</h3>
                <p>Please wait while we redirect you to our secure payment processor...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentScreen;
